import { useState, useEffect } from 'react';
import BlogTile from '../components/BlogTile';
import { useFetchBlogs, useDeleteBlog } from '../features/blog';
import { useUserState } from '../features/user/operations/UserStateProvider';
import { useSafeThemeClasses } from '@/shared/theme';
import { logger } from '@/shared/utils';
import { SEO } from '@/shared/components/SEO';
import { useConfirmDialog } from '@/shared/components/ui';

export default function BlogPage() {
	const theme = useSafeThemeClasses();
	const [isVisible, setIsVisible] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('All');
	const [sort, setSort] = useState<'date_desc' | 'date_asc' | 'popularity' | 'title'>('date_desc');
	const { isAuthenticated } = useUserState();

	// Use isolated blog features
	const { blogs, loading, error, total, fetchBlogs } = useFetchBlogs();
	const { deleteBlog } = useDeleteBlog();
	const { confirm, ConfirmDialog } = useConfirmDialog();

	// Categories for filtering (could be moved to a separate feature)
	const categories = ['All', 'Technology', 'Design', 'Development', 'Innovation'];

	// Fetch blogs when filters change
	useEffect(() => {
		const timeoutId = setTimeout(() => {		fetchBlogs({
			search: searchTerm,
			category: selectedCategory,
			sort,
		});
		}, 300); // Debounce API calls

		return () => clearTimeout(timeoutId);
	}, [searchTerm, selectedCategory, sort, fetchBlogs]);

	useEffect(() => {
		setIsVisible(true);
		logger.debug('Blog page accessed', 'BlogPage');
	}, []);

	const handleDeleteBlog = async (id: string) => {
		const ok = await confirm({
			title: 'Delete this post?',
			message: 'This permanently deletes the post and cannot be undone.',
			confirmLabel: 'Delete',
			danger: true,
			requireTypedWord: 'DELETE',
		});
		if (!ok) return;
		logger.debug('Blog delete initiated', 'BlogPage', { blogId: id });
		await deleteBlog(id);
	};
	return (
		<div className={`min-h-screen pt-24 pb-16 relative ${theme.background}`}>
			<SEO title="Blog" description="Articles and writing." path="/blog" />
			{/* Elevated glass slab - Main body container - Toned down for less distraction */}
			<div className={`absolute inset-4 lg:inset-8 rounded-3xl pointer-events-none z-0 ${theme.container}`}></div>
			
			{/* Content wrapper */}
			<div className="relative z-10">
				<div
					className={`max-w-6xl mx-auto px-4 sm:px-6 relative transition-all duration-1000 ${
						isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
					}`}>
				{/* Page Header - Reduced size */}
				<div className='mb-8 text-center'>
					<h1 className={`text-3xl md:text-4xl font-bold mb-3 ${theme.textHeading}`}>Our Blog</h1>
					<p className={`text-base md:text-lg max-w-2xl mx-auto ${theme.textSecondary}`}>Insights, thoughts, and explorations on technology, design, and innovation.</p>
				</div>

				{/* Search and Filters */}
				<div className={`mb-12 rounded-2xl p-6 ${theme.card}`}>
					<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
						{/* Search */}
						<div className='relative'>
							<input
								type='text'
								placeholder='Search posts...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className={`w-full pl-10 ${theme.input} ${theme.textPrimary}`}
							/>
							<svg
								className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${theme.textMuted}`}
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
							</svg>
						</div>

						{/* Category Filter */}
						<select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={`w-full ${theme.input} ${theme.textPrimary}`}>
							{categories.map((category) => (
								<option key={category} value={category}>
									{category}
								</option>
							))}
						</select>

						{/* Sort */}
						<select						value={sort}
						onChange={(e) => setSort(e.target.value as 'date_desc' | 'date_asc' | 'popularity' | 'title')}
							className={`w-full ${theme.input} ${theme.textPrimary}`}
						>						<option value='date_desc'>Newest First</option>
						<option value='date_asc'>Oldest First</option>
						<option value='title'>Sort by Title</option>
						<option value='popularity'>Sort by Popularity</option>
						</select>
					</div>

					{/* Results count */}
					<div className={`mt-4 text-sm ${theme.textMuted}`}>
						{loading ? 'Loading...' : (() => {
							const postPlural = total !== 1 ? 's' : '';
							return `${total} post${postPlural} found`;
						})()}
					</div>
				</div>

				{/* Error Message */}
				{error && (
					<div className={`mb-8 p-4 rounded-lg ${theme.alertError}`}>
						<p>Error: {error}</p>
					</div>
				)}

				{/* Loading Skeleton */}
				{loading && (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
						{Array.from({ length: 6 }, (_, i) => (
							<div key={`blog-skeleton-${i}`} className='animate-pulse'>
								<div className={`h-48 rounded-t-xl ${theme.card}`}></div>
								<div className='p-6'>
									<div className={`h-4 rounded mb-4 ${theme.card}`}></div>
									<div className={`h-6 rounded mb-2 ${theme.card}`}></div>
									<div className={`h-16 rounded mb-4 ${theme.card}`}></div>
									<div className={`h-4 rounded ${theme.card}`}></div>
								</div>
							</div>
						))}
					</div>
				)}

				{/* Blog Posts Grid */}
				{!loading && (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
						{blogs.map((post) => (
							<BlogTile key={post.id} post={post} onDelete={isAuthenticated ? handleDeleteBlog : undefined} />
						))}
					</div>
				)}

				{/* No results message */}
				{!loading && blogs.length === 0 && !error && (
					<div className='text-center py-16'>
						<svg className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} fill='none' stroke='currentColor' viewBox='0 0 24 24'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.02-5.17-2.517M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
							/>
						</svg>
						<h3 className={`text-xl font-semibold mb-2 ${theme.textHeading}`}>No posts found</h3>
						<p className={theme.textSecondary}>Try adjusting your search or filter criteria.</p>
					</div>
				)}
				</div>
			</div>
			<ConfirmDialog />
		</div>
	);
}
